import axios from 'axios';
import React, { ReactNode, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getBongoUrl } from '../../../common/utils/bongo';
import type { Credentials } from '../types';

type BigQueryContextType = {
  tenant: number;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  getCredentials: () => Promise<Credentials>;
  credentials: Credentials;
  setCredentials: (credentials: Credentials) => void;
  isAuthenticated: () => boolean;
  testCredentials: () => Promise<boolean>;
  getDatasets: () => Promise<string[]>;
  getTables: () => Promise<string[]>;
  testTableMapping: () => Promise<boolean>;
  getColumns: () => Promise<Record<string, string[]>>;
  save: () => Promise<boolean>;
  clearCredentials: () => void;
};

export const BigQueryContext = createContext<BigQueryContextType>(null);

export function BigQueryProvider({
  tenant,
  children,
}: {
  tenant: number;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = React.useState<Credentials>(null);
  const [editMode, setEditMode] = React.useState(false);

  function isAuthenticated(): boolean {
    if (!credentials) return false;
    if (credentials.auth_type === 'service_account') {
      return Boolean(
        credentials.service_account?.client_email &&
          credentials.service_account?.private_key
      );
    }
    return Boolean(credentials.tokens);
  }

  async function getCredentials(): Promise<Credentials> {
    try {
      const response = await axios.get<Credentials>(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/credentials`
      );

      if (Object.keys(response.data).length === 0) return null;

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response.status === 404) {
        return null;
      }

      throw error;
    }
  }

  async function getDatasets(): Promise<string[]> {
    const response = await axios.post<string[]>(
      `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/get_datasets`,
      credentials
    );

    return response.data;
  }

  async function getTables(): Promise<string[]> {
    const response = await axios.post<string[]>(
      `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/get_tables`,
      credentials
    );

    return response.data;
  }

  async function getColumns(): Promise<Record<string, string[]>> {
    const response = await axios.post(
      `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/get_columns`,
      credentials
    );

    return response.data;
  }

  async function testCredentials(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/ping`,
        {
          auth_type: credentials.auth_type,
          tokens: credentials.tokens,
          service_account: credentials.service_account,
          projectId: credentials.projectId,
          dataset: credentials.dataset,
          location: credentials.location,
        }
      );

      return response?.data?.tested ?? false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function testTableMapping(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/ping`,
        {
          auth_type: credentials.auth_type,
          tokens: credentials.tokens,
          service_account: credentials.service_account,
          projectId: credentials.projectId,
          dataset: credentials.dataset,
          location: credentials.location,
          tableNames: credentials.tableNames,
        }
      );

      return response?.data?.tested ?? false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function save(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/bigquery/credentials`,
        credentials,
        { params: { activate_pull: true } }
      );

      queryClient.setQueryData(
        ['bigquery', 'credentials', tenant],
        response.data
      );

      return true;
    } catch (error) {
      console.error(error);

      return false;
    }
  }

  function clearCredentials() {
    queryClient.invalidateQueries(['bigquery', 'credentials', tenant]);
  }

  return (
    <BigQueryContext.Provider
      value={{
        tenant,
        editMode,
        setEditMode,
        getCredentials,
        credentials,
        setCredentials,
        isAuthenticated,
        testCredentials,
        getDatasets,
        getTables,
        testTableMapping,
        getColumns,
        save,
        clearCredentials,
      }}
    >
      {children}
    </BigQueryContext.Provider>
  );
}

export function useBigQueryContext() {
  const bigQueryContext = useContext(BigQueryContext);

  if (!bigQueryContext)
    throw new Error(
      'useBigQueryContext was used outside BigQueryContext provider'
    );

  return bigQueryContext;
}
